import React, { useState } from 'react';
import {
  Scale, FolderOpen, UploadCloud, ShieldCheck,
  FileText, CheckCircle2, ChevronRight, Zap,
  Landmark, Briefcase, Gavel
} from 'lucide-react';

export default function AberturaLaudoOrquestrador({ onConcluir }) {
  const [etapa, setEtapa] = useState(1);
  const [isProcessando, setIsProcessando] = useState(false);
  const [erro, setErro] = useState(null);

  const [dados, setDados] = useState({
    objetivo: null,
    finalidade: null,
    esfera: null,
    protocolo: '',
    requerente: null
  });

  const gerarRastroID = (esfera) => {
    const ano = new Date().getFullYear();
    const prefixo = esfera === 'JUDICIAL' ? 'JUD' : 'EXT';
    return `MH-${prefixo}-${ano}-TEMP`;
  };

  const handleObjetivo = (obj) => {
    setDados(prev => ({ ...prev, objetivo: obj }));
    setEtapa(2);
  };

  const handleFinalidade = (fin) => {
    setDados(prev => ({ ...prev, finalidade: fin }));
    setEtapa(3);
  };

  const handleEsfera = (esf) => {
    const protocolo = gerarRastroID(esf);
    setDados(prev => ({ ...prev, esfera: esf, protocolo }));
    setEtapa(4);
  };

  const handleCapturaOCR = () => {
    setIsProcessando(true);
    setTimeout(() => {
      setDados(prev => ({ ...prev, requerente: 'REQUERENTE IDENTIFICADO' }));
      setIsProcessando(false);
    }, 1200);
  };

  const salvarLaudo = async () => {
    setErro(null);
    setIsProcessando(true);

    try {
      const resp = await fetch('/api/laudos/criar.php', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          objetivo:   dados.objetivo,
          finalidade: dados.finalidade,
          esfera:     dados.esfera,
          requerente: dados.requerente
        })
      });

      const json = await resp.json();

      if (!resp.ok || !json.success) {
        throw new Error(json.error || 'Erro ao salvar.');
      }

      if (typeof onConcluir === 'function') {
        onConcluir(json);
      }

    } catch (e) {
      console.error('Erro ao salvar laudo:', e);
      setErro(e.message || 'Erro ao salvar laudo.');
      setIsProcessando(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-8">

      {/* HEADER */}
      <div className="flex justify-between items-center mb-8 px-6">
        <div className="flex items-center gap-2">
          <Zap size={14} className="text-blue-500 fill-blue-500" />
          <span className="text-[9px] font-black text-slate-500 uppercase tracking-[0.3em]">
            Abertura de Laudo
          </span>
        </div>
        <div className="text-[9px] font-black text-slate-500 uppercase tracking-[0.3em]">
          Etapa {etapa} de 4
        </div>
      </div>

      {/* ETAPA 1 — OBJETIVO */}
      {etapa === 1 && (
        <section className="animate-in slide-in-from-bottom-10 duration-700">
          <div className="text-center mb-12">
            <h2 className="text-white font-black text-5xl uppercase italic tracking-tighter mb-4">
              Objetivo
            </h2>
            <p className="text-blue-500 font-black uppercase text-[10px] tracking-[0.5em]">
              NBR 14653-1 • Item 7
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4">
            {['VALOR DE MERCADO', 'GARANTIA REAL', 'INDENIZATÓRIO', 'LIQUIDAÇÃO FORÇADA'].map(opt => (
              <button
                key={opt}
                onClick={() => handleObjetivo(opt)}
                className="group p-8 bg-white/5 border-4 border-white/5 rounded-[3rem] font-black uppercase text-sm text-slate-400 hover:border-blue-600 hover:text-blue-600 transition-all flex justify-between items-center"
              >
                <span className="tracking-widest">{opt}</span>
                <ChevronRight className="opacity-0 group-hover:opacity-100 transition-all text-blue-600" />
              </button>
            ))}
          </div>
        </section>
      )}

      {/* ETAPA 2 — FINALIDADE */}
      {etapa === 2 && (
        <section className="animate-in slide-in-from-right-10 duration-700">
          <div className="text-center mb-12">
            <h2 className="text-white font-black text-5xl uppercase italic tracking-tighter mb-4">
              Finalidade
            </h2>
            <p className="text-blue-500 font-black uppercase text-[10px] tracking-[0.5em]">
              Uso do Laudo
            </p>
          </div>
          <div className="grid grid-cols-3 gap-8">
            {[
              { id: 'JUDICIAL', icon: Gavel, label: 'Judicial' },
              { id: 'BANCARIA', icon: Landmark, label: 'Bancária' },
              { id: 'ADMINISTRATIVA', icon: Briefcase, label: 'Administrativa' }
            ].map(({ id, icon: Icon, label }) => (
              <button
                key={id}
                onClick={() => handleFinalidade(id)}
                className="group p-10 bg-white/5 rounded-[4rem] text-white flex flex-col items-center gap-6 hover:bg-blue-600 transition-all"
              >
                <Icon size={60} strokeWidth={1} className="group-hover:scale-110 transition-transform" />
                <span className="font-black uppercase text-sm tracking-tighter">{label}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* ETAPA 3 — ESFERA */}
      {etapa === 3 && (
        <section className="animate-in slide-in-from-right-10 duration-700">
          <div className="text-center mb-12">
            <h2 className="text-white font-black text-5xl uppercase italic tracking-tighter mb-4">
              Esfera
            </h2>
            <p className="text-blue-500 font-black uppercase text-[10px] tracking-[0.5em]">
              Competência Jurídica
            </p>
          </div>
          <div className="grid grid-cols-2 gap-8">
            <button
              onClick={() => handleEsfera('JUDICIAL')}
              className="group p-16 bg-white/5 rounded-[4.5rem] text-white flex flex-col items-center gap-6 hover:bg-blue-600 transition-all"
            >
              <Scale size={70} strokeWidth={1} />
              <span className="font-black uppercase text-xl tracking-tighter">Judicial</span>
            </button>
            <button
              onClick={() => handleEsfera('EXTRA')}
              className="group p-16 bg-white/5 rounded-[4.5rem] text-white flex flex-col items-center gap-6 hover:bg-blue-600 transition-all"
            >
              <FolderOpen size={70} strokeWidth={1} />
              <span className="font-black uppercase text-xl tracking-tighter">Extrajudicial</span>
            </button>
          </div>
        </section>
      )}

      {/* ETAPA 4 — CONFIRMAÇÃO */}
      {etapa === 4 && (
        <section className="animate-in zoom-in-95 duration-700 text-center">
          <div className="bg-slate-900 p-12 rounded-[4.5rem] mb-8">
            <p className="text-blue-500 font-black uppercase text-[10px] tracking-[0.6em] mb-4">
              Protocolo Gerado
            </p>
            <h3 className="text-white text-5xl font-black italic tracking-tighter">
              {dados.protocolo}
            </h3>
          </div>

          <div className="bg-white/5 border-4 border-dashed border-white/10 rounded-[3rem] p-12 mb-8">
            <p className="text-slate-400 font-black uppercase text-xs tracking-widest mb-6">
              Confirmar dados
            </p>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-slate-500 text-[10px] uppercase">Objetivo</p>
                <p className="text-white font-black">{dados.objetivo}</p>
              </div>
              <div>
                <p className="text-slate-500 text-[10px] uppercase">Finalidade</p>
                <p className="text-white font-black">{dados.finalidade}</p>
              </div>
              <div>
                <p className="text-slate-500 text-[10px] uppercase">Esfera</p>
                <p className="text-white font-black">{dados.esfera}</p>
              </div>
            </div>
          </div>

          {erro && (
            <p className="text-red-500 font-black uppercase text-xs tracking-widest mb-6">
              ⚠️ {erro}
            </p>
          )}

          <button
            onClick={salvarLaudo}
            disabled={isProcessando}
            className="bg-emerald-500 text-white px-20 py-8 rounded-full font-black uppercase text-sm tracking-[0.4em] hover:bg-emerald-400 disabled:opacity-50 transition-all"
          >
            {isProcessando ? 'Salvando...' : 'Criar Laudo'}
          </button>
        </section>
      )}
    </div>
  );
}